import React, { memo, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  LogOut,
  BarChart,
  Settings,
  FileSpreadsheet,
  MessageSquare,
  HelpCircle,
  Mail,
  Activity,
  Send,
  ClipboardList
} from 'lucide-react';
import { useAuth } from '../../lib/auth';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

/**
 * Composant optimisé pour un élément de menu
 * Utilise React.memo pour éviter les re-renders inutiles
 */
const MenuItemComponent = memo<{
  item: MenuItem;
  isActive: boolean;
}>(({ item, isActive }) => {
  const Icon = item.icon;

  return (
    <li>
      <Link
        to={item.path}
        className={`flex items-center px-4 py-3 rounded-lg transition-colors duration-200 group ${
          isActive
            ? 'bg-blue-50 text-blue-600'
            : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <Icon className="w-5 h-5 mr-3" />
        <div>
          <div className="font-medium">{item.label}</div>
          <div className="text-xs text-gray-500 group-hover:text-gray-700">
            {item.description}
          </div>
        </div>
      </Link>
    </li>
  );
});

MenuItemComponent.displayName = 'MenuItemComponent';

/**
 * Composant optimisé pour le bouton de déconnexion
 */
const LogoutButton = memo<{
  onLogout: () => void;
}>(({ onLogout }) => (
  <div className="p-4 border-t">
    <button
      onClick={onLogout}
      className="flex items-center w-full px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
    >
      <LogOut className="w-5 h-5 mr-3" />
      <span>Déconnexion</span>
    </button>
  </div>
));

LogoutButton.displayName = 'LogoutButton';

/**
 * Composant AdminMenu optimisé avec React.memo
 * Améliore les performances en évitant les re-renders inutiles
 */
const AdminMenuOptimized = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Mémorisation des éléments de menu pour éviter les recalculs
  const menuItems = useMemo<MenuItem[]>(() => [
    {
      path: '/mon-espace/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Vue d\'ensemble'
    },
    {
      path: '/mon-espace/prospects',
      label: 'Prospects',
      icon: Users,
      description: 'Gestion des prospects'
    },
    {
      path: '/mon-espace/quiz',
      label: 'Quiz',
      icon: HelpCircle,
      description: 'Gestion du quiz'
    },
    {
      path: '/mon-espace/analytics',
      label: 'Analytics',
      icon: BarChart,
      description: 'Statistiques et rapports'
    },
    {
      path: '/mon-espace/monitoring',
      label: 'Monitoring',
      icon: Activity,
      description: 'Observabilité et métriques'
    },
    {
      path: '/mon-espace/campaigns',
      label: 'Campaigns',
      icon: FileSpreadsheet,
      description: 'Gestion des campagnes'
    },
    {
      path: '/mon-espace/email-campaigns',
      label: 'Emelia.io',
      icon: Mail,
      description: 'Campagnes email'
    },
    {
      path: '/mon-espace/referrals',
      label: 'Redirections',
      icon: Send,
      description: 'Gestion des emails référés'
    },
    {
      path: '/mon-espace/audit-requests',
      label: 'Demandes Audit SEO',
      icon: ClipboardList,
      description: 'Demandes d\'audit depuis /monscore/'
    },
    {
      path: '/mon-espace/messages',
      label: 'Messages',
      icon: MessageSquare,
      description: 'Centre de messages'
    },
    {
      path: '/mon-espace/settings',
      label: 'Settings',
      icon: Settings,
      description: 'Configuration'
    }
  ], []);

  // Fonction optimisée pour vérifier si un chemin est actif
  const isActive = useCallback((path: string) => {
    return location.pathname === path;
  }, [location.pathname]);

  // Handler optimisé pour la déconnexion
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logout, navigate]);

  // Mémorisation des éléments de menu rendus
  const renderedMenuItems = useMemo(() => 
    menuItems.map((item) => (
      <MenuItemComponent
        key={item.path}
        item={item}
        isActive={isActive(item.path)}
      />
    )), 
    [menuItems, isActive]
  );

  return (
    <div className="bg-white border-r h-full w-64 fixed left-0 top-0 flex flex-col">
      <div className="p-6 border-b flex justify-center">
        <img 
          src="/images/smart_impact_logo.jpg" 
          alt="Smart Impact" 
          className="h-12" 
        />
      </div>

      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-2">
          {renderedMenuItems}
        </ul>
      </nav>

      <LogoutButton onLogout={handleLogout} />
    </div>
  );
});

AdminMenuOptimized.displayName = 'AdminMenuOptimized';

export default AdminMenuOptimized;
